import React, { useEffect, useState, useRef } from 'react';
import { cn } from '../../lib/utils';
import { CommonLayoutProps } from './types';
import CommonSidebar from './CommonSidebar';
import CommonTopBar from './CommonTopBar';
import { isAdministrator } from '../../client/lib/platformRole';
import { LandingSecondaryPanel } from './LandingSecondaryPanel';
import { useOrgNavMode } from '../../client/contexts/OrgNavModeContext';

const SECONDARY_PANEL_WIDTH = 224; // 14rem / w-56

function CommonLayout({
  children,
  config,
  showViewSwitcher = false,
  activeView = 'cards',
  onViewChange,
  forceSidebarCollapse = false,
  user
}: CommonLayoutProps) {
  const { orgTypeKey } = useOrgNavMode();
  const isLandingMode = !!(orgTypeKey?.startsWith('landing-'));

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') {
      return !forceSidebarCollapse;
    }
    const isDesktop = window.innerWidth >= 1024;
    return isDesktop && !forceSidebarCollapse;
  });

  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= 1024;
  });

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      if (desktop && !forceSidebarCollapse) {
        setIsSidebarOpen(true);
      } else if (!desktop) {
        setIsSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [forceSidebarCollapse]);

  const handleNavigate = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <div
      className="h-screen bg-background relative"
      style={{
        '--header-height': config.headerHeight,
        '--sidebar-width': (isSidebarOpen && isDesktop) ? `${config.sidebarWidth}px` : '0px',
        '--secondary-panel-width': (isLandingMode && isSidebarOpen && isDesktop) ? `${SECONDARY_PANEL_WIDTH}px` : '0px',
        '--tab-bar-height': '3.5rem',
      } as React.CSSProperties}
    >
      <CommonSidebar
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        config={config}
        onNavigate={handleNavigate}
        user={user}
      />

      {/* Mobile Sidebar Overlay */}
      {!isDesktop && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Secondary landing-type panel — slides in to the right of the primary sidebar */}
      {isDesktop && (
        <LandingSecondaryPanel
          primarySidebarWidth={(isSidebarOpen && isDesktop) ? config.sidebarWidth : 0}
          isVisible={isLandingMode && isSidebarOpen && isDesktop}
        />
      )}

      <CommonTopBar
        isSidebarOpen={isSidebarOpen}
        onSidebarToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        config={config}
        activeView={showViewSwitcher ? activeView : undefined}
        onViewChange={showViewSwitcher ? onViewChange : undefined}
      />

      <div
        className={cn(
          'flex flex-col h-screen overflow-hidden transition-all duration-300',
          `pt-[var(--header-height)]`,
        )}
        style={isDesktop && isSidebarOpen ? {
          marginLeft: isLandingMode
            ? `${config.sidebarWidth + SECONDARY_PANEL_WIDTH}px`
            : `${config.sidebarWidth}px`
        } : undefined}
      >
        <main className="flex-1 overflow-y-auto pb-16 lg:pb-0">
          {children}
        </main>
      </div>
    </div>
  );
}

// Memoize the layout to prevent sidebar from remounting on page navigation
export default React.memo(CommonLayout, (prevProps, nextProps) => {
  // Re-render only if config reference changes (not deep equal)
  // This allows the layout to persist across page navigations within the same section
  return prevProps.config === nextProps.config &&
         prevProps.showViewSwitcher === nextProps.showViewSwitcher &&
         prevProps.activeView === nextProps.activeView &&
         prevProps.forceSidebarCollapse === nextProps.forceSidebarCollapse &&
         !!prevProps.user === !!nextProps.user &&
         isAdministrator(prevProps.user) === isAdministrator(nextProps.user);
});



